import React, { useState, useEffect } from 'react';
import { authFetch } from '../services/api';
import './ApplicationDependencies.css';

// Types
interface ApplicationDependency {
  dependency_id: number;
  integration_group_id: string;
  source_application_id: string;
  target_application_id: string;
  source_application_name?: string;
  target_application_name?: string;
  source_disposition?: string;
  target_disposition?: string;
  integration_group_name?: string;
  dependency_type: 'finish_to_start' | 'phase_based';
  source_phase_name?: string;
  target_phase_name?: string;
  overlap_days?: number;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

interface IntegrationGroup {
  integration_group_id: string;
  integration_group_description: string;
  application_count: number;
}

interface Application {
  application_id: string;
  description: string;
  disposition?: string;
}

// Helper function to get disposition badge color (consistent with table display)
const getDispositionColor = (disposition?: string) => {
  switch (disposition) {
    case 'Retire': return { bg: '#fef2f2', color: '#dc2626' };      // Red
    case 'Re-Platform': return { bg: '#fffbeb', color: '#f59e0b' }; // Amber
    case 'Re-Design': return { bg: '#eff6ff', color: '#3b82f6' };   // Blue
    case 'Retain': return { bg: '#ecfdf5', color: '#10b981' };      // Green
    default: return { bg: '#f5f5f5', color: '#757575' };
  }
};

// Helper function to get text color for disposition (for table display)
const getDispositionTextColor = (disposition?: string) => {
  switch (disposition) {
    case 'Retire': return '#dc2626';
    case 'Re-Platform': return '#f59e0b';
    case 'Re-Design': return '#3b82f6';
    case 'Retain': return '#10b981';
    default: return '#1976d2';
  }
};

// Valid phases
const PHASES = [
  'Assessment & Planning',
  'Design & Architecture',
  'Migration Execution',
  'Testing & Validation',
  'Go-Live & Optimization'
];

const ApplicationDependencies: React.FC = () => {
  // State
  const [dependencies, setDependencies] = useState<ApplicationDependency[]>([]);
  const [integrationGroups, setIntegrationGroups] = useState<IntegrationGroup[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [filterSourceApp, setFilterSourceApp] = useState('');
  const [filterTargetApp, setFilterTargetApp] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit' | 'view'>('add');
  const [editingDependency, setEditingDependency] = useState<ApplicationDependency | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    integration_group_id: '',
    source_application_id: '',
    target_application_id: '',
    dependency_type: 'finish_to_start' as 'finish_to_start' | 'phase_based',
    source_phase_name: 'Assessment & Planning',
    target_phase_name: 'Assessment & Planning',
    notes: ''
  });

  // Dropdown state
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const [showTargetDropdown, setShowTargetDropdown] = useState(false);

  // Load data on mount
  useEffect(() => {
    loadDependencies();
    loadIntegrationGroups();
  }, []);

  // Load applications when integration group changes
  useEffect(() => {
    if (formData.integration_group_id) {
      loadApplicationsForGroup(formData.integration_group_id);
    } else {
      setApplications([]);
    }
  }, [formData.integration_group_id]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.custom-dropdown')) {
        setShowGroupDropdown(false);
        setShowSourceDropdown(false);
        setShowTargetDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const loadDependencies = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch('/api/application-dependencies');
      const result = await response.json();
      if (result.success) {
        setDependencies(result.data || []);
      } else {
        setError(result.error || 'Failed to load dependencies');
      }
    } catch (err) {
      console.error('Error loading dependencies:', err);
      setError('Error loading dependencies');
    } finally {
      setLoading(false);
    }
  };

  const loadIntegrationGroups = async () => {
    try {
      const response = await authFetch('/api/application-dependencies/lookup/integration-groups');
      const result = await response.json();
      if (result.success) {
        setIntegrationGroups(result.data || []);
      }
    } catch (err) {
      console.error('Error loading integration groups:', err);
    }
  };

  const loadApplicationsForGroup = async (groupId: string) => {
    try {
      const response = await authFetch(`/api/application-dependencies/lookup/applications/${groupId}`);
      const result = await response.json();
      if (result.success) {
        setApplications(result.data || []);
      }
    } catch (err) {
      console.error('Error loading applications:', err);
    }
  };

  const openAddModal = () => {
    setModalMode('add');
    setEditingDependency(null);
    setFormData({
      integration_group_id: '',
      source_application_id: '',
      target_application_id: '',
      dependency_type: 'finish_to_start',
      source_phase_name: 'Assessment & Planning',
      target_phase_name: 'Assessment & Planning',
      notes: ''
    });
    setShowModal(true);
  };

  const openEditModal = (dep: ApplicationDependency) => {
    setModalMode('edit');
    setEditingDependency(dep);
    setFormData({
      integration_group_id: dep.integration_group_id,
      source_application_id: dep.source_application_id,
      target_application_id: dep.target_application_id,
      dependency_type: dep.dependency_type,
      source_phase_name: dep.source_phase_name || 'Assessment & Planning',
      target_phase_name: dep.target_phase_name || 'Assessment & Planning',
      notes: dep.notes || ''
    });
    setShowModal(true);
  };

  const openViewModal = (dep: ApplicationDependency) => {
    setModalMode('view');
    setEditingDependency(dep);
    setFormData({
      integration_group_id: dep.integration_group_id,
      source_application_id: dep.source_application_id,
      target_application_id: dep.target_application_id,
      dependency_type: dep.dependency_type,
      source_phase_name: dep.source_phase_name || 'Assessment & Planning',
      target_phase_name: dep.target_phase_name || 'Assessment & Planning',
      notes: dep.notes || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingDependency(null);
    setShowGroupDropdown(false);
    setShowSourceDropdown(false);
    setShowTargetDropdown(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.integration_group_id || !formData.source_application_id || !formData.target_application_id) {
      alert('Please fill in all required fields');
      return;
    }

    if (formData.source_application_id === formData.target_application_id) {
      alert('Source and Target applications cannot be the same');
      return;
    }

    // Check for duplicate dependency (only for add mode)
    if (modalMode === 'add') {
      const existingDuplicate = dependencies.find(dep =>
        dep.source_application_id === formData.source_application_id &&
        dep.target_application_id === formData.target_application_id
      );
      if (existingDuplicate) {
        alert(`A dependency already exists from ${formData.source_application_id} to ${formData.target_application_id}. Each Source→Target combination can only exist once.`);
        return;
      }

      // Check for circular dependency
      const existingReverse = dependencies.find(dep =>
        dep.source_application_id === formData.target_application_id &&
        dep.target_application_id === formData.source_application_id
      );
      if (existingReverse) {
        alert(`Cannot create dependency: A reverse dependency already exists (${formData.target_application_id} → ${formData.source_application_id}). This would create a circular dependency loop.`);
        return;
      }
    }

    try {
      const payload = {
        integration_group_id: formData.integration_group_id,
        source_application_id: formData.source_application_id,
        target_application_id: formData.target_application_id,
        dependency_type: formData.dependency_type,
        source_phase_name: formData.dependency_type === 'phase_based' ? formData.source_phase_name : null,
        target_phase_name: formData.dependency_type === 'phase_based' ? formData.target_phase_name : null,
        notes: formData.notes || null
      };

      const url = modalMode === 'edit' && editingDependency
        ? `/api/application-dependencies/${editingDependency.dependency_id}`
        : '/api/application-dependencies';

      const method = modalMode === 'edit' ? 'PUT' : 'POST';

      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success) {
        alert(result.message || 'Dependency saved successfully');
        closeModal();
        loadDependencies();
      } else {
        alert(result.error || 'Failed to save dependency');
      }
    } catch (err) {
      console.error('Error saving dependency:', err);
      alert('Error saving dependency');
    }
  };

  const handleDelete = async (dep: ApplicationDependency) => {
    if (!window.confirm(`Are you sure you want to delete this dependency between ${dep.source_application_id} and ${dep.target_application_id}?`)) {
      return;
    }

    try {
      const response = await authFetch(`/api/application-dependencies/${dep.dependency_id}`, {
        method: 'DELETE'
      });

      const result = await response.json();

      if (result.success) {
        loadDependencies();
      } else {
        alert(result.error || 'Failed to delete dependency');
      }
    } catch (err) {
      console.error('Error deleting dependency:', err);
      alert('Error deleting dependency');
    }
  };

  const getRelationshipDisplay = (dep: ApplicationDependency) => {
    if (dep.dependency_type === 'finish_to_start') {
      return 'Finish→Start';
    }
    // For phase_based, show phase abbreviations
    const sourcePhase = dep.source_phase_name || 'Assessment & Planning';
    const targetPhase = dep.target_phase_name || 'Assessment & Planning';

    const getPhaseAbbrev = (phase: string) => {
      switch (phase) {
        case 'Assessment & Planning': return 'Assess';
        case 'Design & Architecture': return 'Design';
        case 'Migration Execution': return 'Migrate';
        case 'Testing & Validation': return 'Test';
        case 'Go-Live & Optimization': return 'Go-Live';
        default: return phase.substring(0, 6);
      }
    };

    return `${getPhaseAbbrev(sourcePhase)}→${getPhaseAbbrev(targetPhase)}`;
  };

  const filteredDependencies = dependencies.filter(dep => {
    const sourceMatch = !filterSourceApp ||
      dep.source_application_id.toLowerCase().includes(filterSourceApp.toLowerCase()) ||
      (dep.source_application_name || '').toLowerCase().includes(filterSourceApp.toLowerCase());
    const targetMatch = !filterTargetApp ||
      dep.target_application_id.toLowerCase().includes(filterTargetApp.toLowerCase()) ||
      (dep.target_application_name || '').toLowerCase().includes(filterTargetApp.toLowerCase());
    return sourceMatch && targetMatch;
  });

  const selectedGroup = integrationGroups.find(g => g.integration_group_id === formData.integration_group_id);
  const selectedSourceApp = applications.find(a => a.application_id === formData.source_application_id);
  const selectedTargetApp = applications.find(a => a.application_id === formData.target_application_id);

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Application Interdependencies</h1>
            <p className="page-header__subtitle">
              Manage dependencies and relationships between applications
            </p>
          </div>
          <div className="page-header__actions">
            <button className="btn btn-primary" onClick={openAddModal}>
              + Add Dependency
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="master-data-container">
        {/* Filter Section */}
        {dependencies.length > 0 && (
          <div className="md-card" style={{ marginBottom: '16px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '16px', fontWeight: '500', minWidth: '100px' }}>Source App:</label>
                <input
                  type="text"
                  value={filterSourceApp}
                  onChange={(e) => setFilterSourceApp(e.target.value)}
                  placeholder="Filter by source..."
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px',
                    minWidth: '180px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '16px', fontWeight: '500', minWidth: '100px' }}>Target App:</label>
                <input
                  type="text"
                  value={filterTargetApp}
                  onChange={(e) => setFilterTargetApp(e.target.value)}
                  placeholder="Filter by target..."
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px',
                    minWidth: '180px'
                  }}
                />
              </div>
              {(filterSourceApp || filterTargetApp) && (
                <button
                  onClick={() => { setFilterSourceApp(''); setFilterTargetApp(''); }}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  Clear Filters
                </button>
              )}
              <span style={{ fontSize: '16px', color: '#666', marginLeft: 'auto' }}>
                {filteredDependencies.length} dependenc{filteredDependencies.length === 1 ? 'y' : 'ies'}
              </span>
            </div>
          </div>
        )}

        {/* Dependencies Table */}
        <div className="md-card">
          {loading ? (
            <div className="md-loading-state">
              <div className="md-spinner"></div>
              <p>Loading dependencies...</p>
            </div>
          ) : error ? (
            <div className="md-error-state">
              <div className="md-error-icon">⚠️</div>
              <h3>Error Loading Data</h3>
              <p>{error}</p>
              <button className="btn btn-primary" onClick={loadDependencies}>Retry</button>
            </div>
          ) : dependencies.length === 0 ? (
            <div className="md-empty-state">
              <div className="md-empty-icon">🔗</div>
              <h3>No Dependencies Found</h3>
              <p>Create dependencies between applications to establish relationships</p>
              <button className="btn btn-primary" onClick={openAddModal} style={{ marginTop: '16px' }}>
                + Add Dependency
              </button>
            </div>
          ) : (
            <div className="md-table-container">
              <table className="md-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'center', padding: '12px', fontSize: '16px' }}>Source App</th>
                    <th style={{ textAlign: 'left', padding: '12px', fontSize: '16px' }}>Description</th>
                    <th style={{ textAlign: 'center', padding: '12px', fontSize: '16px' }}>Relationship</th>
                    <th style={{ textAlign: 'center', padding: '12px', fontSize: '16px' }}>Target App</th>
                    <th style={{ textAlign: 'left', padding: '12px', fontSize: '16px' }}>Description</th>
                    <th style={{ textAlign: 'center', padding: '12px', fontSize: '16px' }}>Overlap</th>
                    <th style={{ textAlign: 'center', padding: '12px', fontSize: '16px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDependencies.map((dep) => (
                    <tr key={dep.dependency_id}>
                      <td style={{ textAlign: 'center', padding: '12px', fontSize: '16px' }}>
                        <strong style={{ color: getDispositionTextColor(dep.source_disposition) }}>{dep.source_application_id}</strong>
                      </td>
                      <td style={{ textAlign: 'left', padding: '12px', fontSize: '16px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#333' }}>
                        {dep.source_application_name || '-'}
                      </td>
                      <td style={{ textAlign: 'center', padding: '12px', fontSize: '16px' }}>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          backgroundColor: dep.dependency_type === 'finish_to_start' ? '#e3f2fd' : '#fff3e0',
                          color: dep.dependency_type === 'finish_to_start' ? '#1565c0' : '#e65100',
                          fontWeight: '500',
                          fontSize: '16px'
                        }}>
                          {getRelationshipDisplay(dep)}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center', padding: '12px', fontSize: '16px' }}>
                        <strong style={{ color: getDispositionTextColor(dep.target_disposition) }}>{dep.target_application_id}</strong>
                      </td>
                      <td style={{ textAlign: 'left', padding: '12px', fontSize: '16px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#333' }}>
                        {dep.target_application_name || '-'}
                      </td>
                      <td style={{ textAlign: 'center', padding: '12px' }}>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          backgroundColor: dep.dependency_type === 'finish_to_start' ? '#e8f5e9' : '#fff3e0',
                          color: dep.dependency_type === 'finish_to_start' ? '#2e7d32' : '#e65100',
                          fontWeight: '500',
                          fontSize: '16px'
                        }}>
                          {dep.dependency_type === 'finish_to_start' ? 'No Overlap' : 'Overlap'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center', padding: '12px' }}>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                          <button
                            onClick={() => openViewModal(dep)}
                            title="View details"
                            style={{
                              padding: '4px 6px',
                              backgroundColor: 'transparent',
                              color: '#1976d2',
                              border: '1px solid #1976d2',
                              borderRadius: '4px',
                              fontSize: '16px',
                              cursor: 'pointer',
                              minWidth: 'auto'
                            }}
                          >
                            👁️
                          </button>
                          <button
                            onClick={() => openEditModal(dep)}
                            title="Edit dependency"
                            style={{
                              padding: '4px 6px',
                              backgroundColor: 'transparent',
                              color: '#1976d2',
                              border: '1px solid #1976d2',
                              borderRadius: '4px',
                              fontSize: '16px',
                              cursor: 'pointer',
                              minWidth: 'auto'
                            }}
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDelete(dep)}
                            title="Delete dependency"
                            style={{
                              padding: '4px 6px',
                              backgroundColor: 'transparent',
                              color: '#d32f2f',
                              border: '1px solid #d32f2f',
                              borderRadius: '4px',
                              fontSize: '16px',
                              cursor: 'pointer',
                              minWidth: 'auto'
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit/View Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '680px',
            maxWidth: '95vw',
            maxHeight: '85vh',
            overflowY: 'auto',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2)'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '3px solid #1976d2',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '500', color: '#212121' }}>
                {modalMode === 'add' ? 'Add Dependency Relationship' :
                 modalMode === 'edit' ? 'Edit Dependency Relationship' :
                 'View Dependency Details'}
              </h2>
              <button
                onClick={closeModal}
                style={{
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  background: 'transparent',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#757575',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
              {/* Integration Group */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '16px', fontWeight: '500', color: '#424242', marginBottom: '8px' }}>
                  Integration Group *
                </label>
                <div className="custom-dropdown" style={{ position: 'relative' }}>
                  <div
                    onClick={() => {
                      if (modalMode === 'add') {
                        setShowGroupDropdown(!showGroupDropdown);
                        setShowSourceDropdown(false);
                        setShowTargetDropdown(false);
                      }
                    }}
                    style={{
                      padding: '12px 16px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: modalMode !== 'add' ? '#f5f5f5' : 'white',
                      cursor: modalMode === 'add' ? 'pointer' : 'not-allowed',
                      fontSize: '16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span style={{ color: selectedGroup ? '#212121' : '#9e9e9e' }}>
                      {selectedGroup
                        ? `${selectedGroup.integration_group_id} - ${selectedGroup.integration_group_description} (${selectedGroup.application_count} apps)`
                        : 'Select the Integration Group'}
                    </span>
                    {modalMode === 'add' && <span>▼</span>}
                  </div>
                  {showGroupDropdown && modalMode === 'add' && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 1000,
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}>
                      {integrationGroups.length === 0 ? (
                        <div style={{ padding: '12px 16px', color: '#9e9e9e', fontSize: '16px' }}>
                          No integration groups available
                        </div>
                      ) : (
                        integrationGroups.map(group => (
                          <div
                            key={group.integration_group_id}
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                integration_group_id: group.integration_group_id,
                                source_application_id: '',
                                target_application_id: ''
                              }));
                              setShowGroupDropdown(false);
                            }}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              fontSize: '16px',
                              borderBottom: '1px solid #f0f0f0',
                              backgroundColor: formData.integration_group_id === group.integration_group_id ? '#e3f2fd' : 'white'
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = formData.integration_group_id === group.integration_group_id ? '#e3f2fd' : 'white')}
                          >
                            <strong>{group.integration_group_id}</strong> - {group.integration_group_description}
                            <span style={{ float: 'right', color: '#666' }}>({group.application_count} apps)</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Source Application */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '16px', fontWeight: '500', color: '#424242', marginBottom: '8px' }}>
                  Source Application *
                </label>
                <div className="custom-dropdown" style={{ position: 'relative' }}>
                  <div
                    onClick={() => {
                      if (modalMode === 'add' && formData.integration_group_id) {
                        setShowSourceDropdown(!showSourceDropdown);
                        setShowGroupDropdown(false);
                        setShowTargetDropdown(false);
                      }
                    }}
                    style={{
                      padding: '12px 16px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: modalMode !== 'add' ? '#f5f5f5' : (!formData.integration_group_id ? '#fafafa' : 'white'),
                      cursor: modalMode === 'add' && formData.integration_group_id ? 'pointer' : 'not-allowed',
                      fontSize: '16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span style={{ color: selectedSourceApp ? '#212121' : '#9e9e9e', display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      {selectedSourceApp
                        ? (
                          <>
                            <span>{selectedSourceApp.application_id} - {selectedSourceApp.description}</span>
                            {selectedSourceApp.disposition && (
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '10px',
                                fontSize: '16px',
                                fontWeight: '500',
                                backgroundColor: getDispositionColor(selectedSourceApp.disposition).bg,
                                color: getDispositionColor(selectedSourceApp.disposition).color
                              }}>
                                {selectedSourceApp.disposition}
                              </span>
                            )}
                          </>
                        )
                        : (formData.integration_group_id ? 'Select Application' : 'Select Integration Group first')}
                    </span>
                    {modalMode === 'add' && formData.integration_group_id && <span>▼</span>}
                  </div>
                  {showSourceDropdown && modalMode === 'add' && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 1000,
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}>
                      {applications.filter(a => a.application_id !== formData.target_application_id).map(app => (
                        <div
                          key={app.application_id}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, source_application_id: app.application_id }));
                            setShowSourceDropdown(false);
                          }}
                          style={{
                            padding: '12px 16px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            borderBottom: '1px solid #f0f0f0',
                            backgroundColor: formData.source_application_id === app.application_id ? '#e3f2fd' : 'white',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = formData.source_application_id === app.application_id ? '#e3f2fd' : 'white')}
                        >
                          <span><strong>{app.application_id}</strong> - {app.description}</span>
                          {app.disposition && (
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '10px',
                              fontSize: '11px',
                              fontWeight: '500',
                              backgroundColor: getDispositionColor(app.disposition).bg,
                              color: getDispositionColor(app.disposition).color,
                              marginLeft: '8px'
                            }}>
                              {app.disposition}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Target Application */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '16px', fontWeight: '500', color: '#424242', marginBottom: '8px' }}>
                  Target Application *
                </label>
                <div className="custom-dropdown" style={{ position: 'relative' }}>
                  <div
                    onClick={() => {
                      if (modalMode === 'add' && formData.integration_group_id) {
                        setShowTargetDropdown(!showTargetDropdown);
                        setShowGroupDropdown(false);
                        setShowSourceDropdown(false);
                      }
                    }}
                    style={{
                      padding: '12px 16px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: modalMode !== 'add' ? '#f5f5f5' : (!formData.integration_group_id ? '#fafafa' : 'white'),
                      cursor: modalMode === 'add' && formData.integration_group_id ? 'pointer' : 'not-allowed',
                      fontSize: '16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span style={{ color: selectedTargetApp ? '#212121' : '#9e9e9e', display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      {selectedTargetApp
                        ? (
                          <>
                            <span>{selectedTargetApp.application_id} - {selectedTargetApp.description}</span>
                            {selectedTargetApp.disposition && (
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '10px',
                                fontSize: '16px',
                                fontWeight: '500',
                                backgroundColor: getDispositionColor(selectedTargetApp.disposition).bg,
                                color: getDispositionColor(selectedTargetApp.disposition).color
                              }}>
                                {selectedTargetApp.disposition}
                              </span>
                            )}
                          </>
                        )
                        : (formData.integration_group_id ? 'Select Application' : 'Select Integration Group first')}
                    </span>
                    {modalMode === 'add' && formData.integration_group_id && <span>▼</span>}
                  </div>
                  {showTargetDropdown && modalMode === 'add' && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 1000,
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}>
                      {applications.filter(a => a.application_id !== formData.source_application_id).map(app => (
                        <div
                          key={app.application_id}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, target_application_id: app.application_id }));
                            setShowTargetDropdown(false);
                          }}
                          style={{
                            padding: '12px 16px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            borderBottom: '1px solid #f0f0f0',
                            backgroundColor: formData.target_application_id === app.application_id ? '#e3f2fd' : 'white',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = formData.target_application_id === app.application_id ? '#e3f2fd' : 'white')}
                        >
                          <span><strong>{app.application_id}</strong> - {app.description}</span>
                          {app.disposition && (
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '10px',
                              fontSize: '11px',
                              fontWeight: '500',
                              backgroundColor: getDispositionColor(app.disposition).bg,
                              color: getDispositionColor(app.disposition).color,
                              marginLeft: '8px'
                            }}>
                              {app.disposition}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Dependency Type */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '16px', fontWeight: '500', color: '#424242', marginBottom: '12px' }}>
                  Dependency Type *
                </label>
                <div style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '16px',
                  backgroundColor: modalMode === 'view' ? '#f5f5f5' : 'white'
                }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px', cursor: modalMode !== 'view' ? 'pointer' : 'default' }}>
                    <input
                      type="radio"
                      name="dependency_type"
                      value="finish_to_start"
                      checked={formData.dependency_type === 'finish_to_start'}
                      onChange={() => setFormData(prev => ({ ...prev, dependency_type: 'finish_to_start' }))}
                      disabled={modalMode === 'view'}
                      style={{ marginTop: '4px', accentColor: '#1976d2' }}
                    />
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: '500', color: '#212121' }}>Sequential (Finish-to-Start)</div>
                      <div style={{ fontSize: '16px', color: '#666', marginTop: '4px' }}>
                        Source application must complete before target application starts
                      </div>
                    </div>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: modalMode !== 'view' ? 'pointer' : 'default' }}>
                    <input
                      type="radio"
                      name="dependency_type"
                      value="phase_based"
                      checked={formData.dependency_type === 'phase_based'}
                      onChange={() => setFormData(prev => ({ ...prev, dependency_type: 'phase_based' }))}
                      disabled={modalMode === 'view'}
                      style={{ marginTop: '4px', accentColor: '#1976d2' }}
                    />
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: '500', color: '#212121' }}>Overlapping Phases</div>
                      <div style={{ fontSize: '16px', color: '#666', marginTop: '4px' }}>
                        Both applications can run in parallel; target starts at specified source phase
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Dependency Type Description */}
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#e3f2fd',
                borderRadius: '4px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '16px' }}>➡️</span>
                <span style={{ fontSize: '16px', color: '#1565c0' }}>
                  {formData.dependency_type === 'finish_to_start'
                    ? 'The target application will start immediately after the source application completes.'
                    : 'The target application will start when the source application reaches the specified phase.'}
                </span>
              </div>

              {/* Phase Selectors (only for phase_based) */}
              {formData.dependency_type === 'phase_based' && (
                <>
                  {/* Source Phase */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '16px', fontWeight: '500', color: '#424242', marginBottom: '8px' }}>
                      Source Phase At Which Target Can Start *
                    </label>
                    <div style={{ display: 'flex', gap: '2px', marginBottom: '4px' }}>
                      {PHASES.map((phase, index) => (
                        <button
                          key={phase}
                          type="button"
                          onClick={() => modalMode !== 'view' && setFormData(prev => ({ ...prev, source_phase_name: phase }))}
                          disabled={modalMode === 'view'}
                          style={{
                            flex: 1,
                            padding: '10px 4px',
                            border: 'none',
                            backgroundColor: formData.source_phase_name === phase ? '#1976d2' : '#f5f5f5',
                            color: formData.source_phase_name === phase ? 'white' : '#424242',
                            cursor: modalMode !== 'view' ? 'pointer' : 'default',
                            fontSize: '11px',
                            fontWeight: '500',
                            borderRadius: index === 0 ? '4px 0 0 4px' : index === PHASES.length - 1 ? '0 4px 4px 0' : '0',
                            transition: 'all 0.2s'
                          }}
                        >
                          {phase.split(' ')[0]}
                        </button>
                      ))}
                    </div>
                    <div style={{ fontSize: '16px', color: '#666' }}>
                      Selected: {formData.source_phase_name}
                    </div>
                  </div>

                  {/* Target Phase (locked) */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '16px', fontWeight: '500', color: '#424242', marginBottom: '8px' }}>
                      Target Application Starting Phase (Locked)
                    </label>
                    <div style={{ display: 'flex', gap: '2px', marginBottom: '4px', opacity: 0.6 }}>
                      {PHASES.map((phase, index) => (
                        <div
                          key={phase}
                          style={{
                            flex: 1,
                            padding: '10px 4px',
                            backgroundColor: phase === 'Assessment & Planning' ? '#9e9e9e' : '#f5f5f5',
                            color: phase === 'Assessment & Planning' ? 'white' : '#bdbdbd',
                            fontSize: '11px',
                            fontWeight: '500',
                            textAlign: 'center',
                            borderRadius: index === 0 ? '4px 0 0 4px' : index === PHASES.length - 1 ? '0 4px 4px 0' : '0'
                          }}
                        >
                          {phase.split(' ')[0]}
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: '16px', color: '#666' }}>
                      Always starts at Assessment & Planning phase
                    </div>
                  </div>
                </>
              )}

              {/* Notes */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '16px', fontWeight: '500', color: '#424242', marginBottom: '8px' }}>
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value.slice(0, 500) }))}
                  placeholder="Add any additional notes about this dependency..."
                  disabled={modalMode === 'view'}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    backgroundColor: modalMode === 'view' ? '#f5f5f5' : 'white'
                  }}
                />
                <div style={{ fontSize: '12px', color: '#666', textAlign: 'right', marginTop: '4px' }}>
                  {formData.notes.length}/500
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                paddingTop: '16px',
                borderTop: '1px solid #e0e0e0'
              }}>
                <button
                  type="button"
                  onClick={closeModal}
                  style={{
                    padding: '10px 20px',
                    fontSize: '16px',
                    fontWeight: '500',
                    backgroundColor: '#f5f5f5',
                    color: '#424242',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {modalMode === 'view' ? 'Close' : 'Cancel'}
                </button>
                {modalMode !== 'view' && (
                  <button
                    type="submit"
                    style={{
                      padding: '10px 20px',
                      fontSize: '16px',
                      fontWeight: '500',
                      backgroundColor: '#1976d2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    {modalMode === 'edit' ? 'Update Dependency' : 'Save Dependency'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationDependencies;
